import type { FullComposeViewState } from 'owa-calendar-compose-types';
import isInPopOutWindow from 'owa-calendar-compose-form-store/lib/selectors/isInPopOutWindow';
import { getComposeStore } from 'owa-calendar-compose-store-manager';
import { QUICK_COMPOSE_FORM_TYPE } from 'owa-calendar-helpers-types/lib/ComposeTypes';
import { resetDayGrid } from 'owa-calendar-scheduling-grid';
import { orchestrator } from 'satcheljs';
import {
    closeComposeForm,
    resetComposeStore,
    updateOpenedForm,
    closeComposeSchedulingAssistant,
} from 'owa-calendar-compose-form-actions';
import { userInteractionAction } from 'owa-calendar-actions';
import { getOpxHostApi } from 'owa-opx';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { lazyRemoveCollabSpaceViewStateFromCollection } from 'owa-calendar-collaborative-space';
import { onCloseFullCompose, onCloseQuickCompose } from 'owa-calendar-compose-form-store';

export default orchestrator(closeComposeForm, async actionMessage => {
    // Log the user interaction action
    userInteractionAction(
        'ViewClosed',
        'EditForm',
        true /* isOrganizer */,
        actionMessage.isMeeting
    );

    const store = getComposeStore();
    const openedForm = store.openedForm;
    const composeViewState = <FullComposeViewState>store.uiState;

    if (isHostAppFeatureEnabled('composeFormSaveCloseHandlers') && !openedForm) {
        // if openedForm is null, then do nothing since the form is already closed.
        // This happens in opx scenarios where we register OpxHostApi.registerFormCloseHandler()
        // and the host calls close when the form is already closed in OWA.
        return;
    }

    if (composeViewState) {
        const isPopout = isInPopOutWindow();
        const shouldResetComposeStoreForUndo = actionMessage.shouldResetComposeStoreForUndo;
        const quickComposeEntrySource = store.metricsInfo.quickComposeEntrySource ?? null;

        // The legacy actions call no-op satchelv2 actions so the stitches (that still use v2 subscriptions) will fire correctly
        if (openedForm == QUICK_COMPOSE_FORM_TYPE) {
            if (shouldResetComposeStoreForUndo) {
                resetComposeStore(null /* formId */);
            } else {
                // Initializing the opened form value to undefined or null hides the compose form
                updateOpenedForm(undefined);
            }

            onCloseQuickCompose(quickComposeEntrySource);
        } else {
            const {
                schedulingGridScenarioId,
                collabSpaceViewStateId,
                showSchedulingAssistant,
            } = composeViewState;

            if (shouldResetComposeStoreForUndo) {
                resetComposeStore(null /* formId */);
            } else {
                // Initializing the opened form value to undefined or null hides the compose form
                updateOpenedForm(undefined);
            }

            resetDayGrid(schedulingGridScenarioId);

            lazyRemoveCollabSpaceViewStateFromCollection.importAndExecute(collabSpaceViewStateId);

            if (showSchedulingAssistant) {
                closeComposeSchedulingAssistant(null /* formId */);
            }

            onCloseFullCompose(quickComposeEntrySource);

            if (isHostAppFeatureEnabled('navigateToView')) {
                // After form is open, then let the host know that the view has changed to the Surface.
                // We don't use deeplink today in opx, so this is okay. In the future, if we start using deeplinks,
                // then we should update this code be skipped if  it is a opx-deeplink
                getOpxHostApi().navigateToView?.('Surface');
            }

            if (isPopout && actionMessage.shouldClosePopOutWindow) {
                window.close();
            }
        }
    }
});
